import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Building, User, FileText, Calendar } from 'lucide-react';
import type { TransportCompany, TransportCompanyForm, ModalState } from '../types/system-parameters.types';

interface TransportCompanyModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: TransportCompanyForm, isEdit?: boolean, itemId?: string) => void;
}

const serviceTypeOptions = [
  { value: 'carga', label: 'Carga' },
  { value: 'pasajeros', label: 'Pasajeros' },
  { value: 'mixto', label: 'Mixto' },
  { value: 'delivery', label: 'Delivery' }
];

export const TransportCompanyModal: React.FC<TransportCompanyModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<TransportCompanyForm>({
    companyName: '',
    ruc: '',
    address: '',
    phone: '',
    email: '',
    contactPerson: '',
    contactPhone: '',
    mtcAuthorization: '',
    authorizationExpiry: '',
    serviceType: []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const company = modalState.item as TransportCompany;
        setFormData({
          companyName: company.additionalData.companyName,
          ruc: company.additionalData.ruc,
          address: company.additionalData.address,
          phone: company.additionalData.phone,
          email: company.additionalData.email || '',
          contactPerson: company.additionalData.contactPerson,
          contactPhone: company.additionalData.contactPhone,
          mtcAuthorization: company.additionalData.mtcAuthorization || '',
          authorizationExpiry: company.additionalData.authorizationExpiry || '',
          serviceType: company.additionalData.serviceType
        });
      } else {
        // Resetear para modo crear
        setFormData({
          companyName: '',
          ruc: '',
          address: '',
          phone: '',
          email: '',
          contactPerson: '',
          contactPhone: '',
          mtcAuthorization: '',
          authorizationExpiry: '',
          serviceType: []
        });
      }
      setErrors({});
    }
  }, [modalState]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validaciones obligatorias
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'El nombre de la empresa es obligatorio';
    }

    if (!formData.ruc.trim()) {
      newErrors.ruc = 'El RUC es obligatorio';
    } else if (!/^20\d{9}$/.test(formData.ruc.trim())) {
      newErrors.ruc = 'El RUC debe tener 11 dígitos y empezar con 20';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'La dirección es obligatoria';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    } else if (!/^\+51\s01-\d{7}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Formato inválido (ej: +51 01-4567890)';
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'El nombre del contacto es obligatorio';
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'El teléfono del contacto es obligatorio';
    } else if (!/^\+51\s9\d{8}$/.test(formData.contactPhone.trim())) {
      newErrors.contactPhone = 'Formato inválido (ej: +51 987654321)';
    }

    if (formData.serviceType.length === 0) {
      newErrors.serviceType = 'Debe seleccionar al menos un tipo de servicio';
    }

    // Validaciones opcionales pero con formato
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    if (formData.authorizationExpiry) {
      const authDate = new Date(formData.authorizationExpiry);
      const today = new Date();
      if (authDate < today) {
        newErrors.authorizationExpiry = 'La fecha de autorización no puede estar vencida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const isEdit = modalState.mode === 'edit';
      const itemId = modalState.item?.id;
      
      onSave(formData, isEdit, itemId);
      onClose();
    } catch (error) {
      console.error('Error al guardar empresa de transporte:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TransportCompanyForm, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleServiceTypeChange = (serviceType: string, checked: boolean) => {
    const currentServices = formData.serviceType;
    
    if (checked) {
      handleInputChange('serviceType', [...currentServices, serviceType]);
    } else {
      handleInputChange('serviceType', currentServices.filter(s => s !== serviceType));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-macartur-primary" />
            {modalState.mode === 'edit' ? 'Editar Empresa de Transporte' : 'Agregar Nueva Empresa de Transporte'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información de la Empresa */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Building className="h-4 w-4" />
              Información de la Empresa
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">
                  Nombre de la Empresa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Transportes Rápidos Lima S.A.C."
                  className={errors.companyName ? 'border-red-500' : ''}
                />
                {errors.companyName && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.companyName}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ruc">
                  RUC <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="ruc"
                  value={formData.ruc}
                  onChange={(e) => handleInputChange('ruc', e.target.value)}
                  placeholder="20123456789"
                  className={errors.ruc ? 'border-red-500' : ''}
                  maxLength={11}
                />
                {errors.ruc && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.ruc}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">
                Dirección <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Av. Argentina 1234, Callao, Lima"
                className={errors.address ? 'border-red-500' : ''}
                rows={2}
              />
              {errors.address && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {errors.address}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">
                  Teléfono <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+51 01-4567890"
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="contacto@empresa.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Información de Contacto */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Persona de Contacto
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactPerson">
                  Nombre del Contacto <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  placeholder="Roberto Vásquez"
                  className={errors.contactPerson ? 'border-red-500' : ''}
                />
                {errors.contactPerson && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.contactPerson}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">
                  Teléfono del Contacto <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  placeholder="+51 987123456"
                  className={errors.contactPhone ? 'border-red-500' : ''}
                />
                {errors.contactPhone && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.contactPhone}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Autorización MTC */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Autorización MTC
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mtcAuthorization">Número de Autorización</Label>
                <Input
                  id="mtcAuthorization"
                  value={formData.mtcAuthorization}
                  onChange={(e) => handleInputChange('mtcAuthorization', e.target.value.toUpperCase())}
                  placeholder="MTC-2023-001234"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="authorizationExpiry">Fecha de Vencimiento</Label>
                <Input
                  id="authorizationExpiry"
                  type="date"
                  value={formData.authorizationExpiry}
                  onChange={(e) => handleInputChange('authorizationExpiry', e.target.value)}
                  className={errors.authorizationExpiry ? 'border-red-500' : ''}
                />
                {errors.authorizationExpiry && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.authorizationExpiry}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Tipos de Servicio */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              Tipos de Servicio <span className="text-red-500">*</span>
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {serviceTypeOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`service-${option.value}`}
                    checked={formData.serviceType.includes(option.value)}
                    onCheckedChange={(checked) => handleServiceTypeChange(option.value, checked as boolean)}
                  />
                  <Label htmlFor={`service-${option.value}`} className="text-sm">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {errors.serviceType && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.serviceType}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-macartur-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (modalState.mode === 'edit' ? 'Actualizar' : 'Agregar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};